import React from 'react';
import { useSelector } from 'react-redux';
import { Drawer } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import styles from './contact-details.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import _ from 'lodash';
import { SectionTitle } from '../design-system/titles';
import { ContactDetailsForm } from './contact-details-form';
import analyticsService from 'src/helpers/analytics.service';
import { colorPalette, ENTITY_TYPES, EVENTS } from 'shared/constants';
import { formatFullName } from 'shared/utils';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { IconButton } from '../design-system';

const ContactDetailsComponent = React.memo(function ContactDetailsComponent({
  contact,
  close,
  open,
  ...props
}) {
  const lang = useSelector(getLang('CONTACT_SCREEN'));
  const closeLabel = useSelector(getLang('ACTION_BUTTON_TOOLTIPS')).CLOSE;
  const isNewContact = _.isEmpty(contact);
  const fullName = formatFullName(contact);
  const title = !isNewContact ? `${fullName}` : lang.ADD_NEW_CONTACT;

  React.useEffect(() => {
    if (!isNewContact && open) {
      const currentContextId = searchContextIdManager.getContextId();

      analyticsService.track(EVENTS.CONTENT.OPEN, {
        id: contact.id,
        entityType: ENTITY_TYPES.contacts,
        contextId: currentContextId,
      });
    }
  }, [contact, isNewContact, open]);

  return (
    <Drawer
      open={open}
      onClose={close}
      size={330}
      placement="right"
      destroyOnHidden
      className="new-design-system"
      closable={false}
      title={
        <div className={styles.header}>
          <SectionTitle title={title} wrapperClassName={styles.title} />
          <IconButton
            tooltipProps={{
              placement: 'left',
            }}
            onClick={close}
            label={closeLabel}
            className={styles.close}>
            <FontAwesomeIcon icon={faXmark} color={colorPalette.primaryMain9} size="xl" />
          </IconButton>
        </div>
      }>
      <ContactDetailsForm
        // A stable key for a new contact: a per-render id remounted the form —
        // wiping what was typed — whenever the contacts list updated while the
        // drawer was open. destroyOnHidden still gives each open a blank form.
        key={`details-form-${contact?.id ?? 'new'}`}
        contact={contact}
        onFinish={close}
        {...props}
      />
    </Drawer>
  );
});

export { ContactDetailsComponent };
